import { useMemo, useState } from 'react';
import type { GlAccountOut } from '../api/gl-accounts';

export type GlAccountSortKey = 'volume' | 'name' | 'transactions' | 'lastSeen';
export type ClassifiedFilter = 'all' | 'classified' | 'unclassified' | 'disagreement';

export interface GlAccountFilters {
  search: string;
  accountType: string; // 'all' or an exact account_type value
  classified: ClassifiedFilter;
}

export function distinctAccountTypes(items: GlAccountOut[]): string[] {
  const seen = new Set<string>();
  for (const item of items) {
    if (item.account_type) seen.add(item.account_type);
  }
  return [...seen].sort((a, b) => a.localeCompare(b));
}

/** The classifier's guess and the practitioner's decision disagree —
 *  exactly the rows that become labelled data for a future model. */
export function isDisagreement(item: GlAccountOut): boolean {
  return (
    item.category != null &&
    item.suggested_category != null &&
    item.category !== item.suggested_category
  );
}

export function filterGlAccounts(
  items: GlAccountOut[],
  { search, accountType, classified }: GlAccountFilters,
): GlAccountOut[] {
  const needle = search.trim().toLowerCase();
  return items.filter((item) => {
    if (accountType !== 'all' && item.account_type !== accountType) return false;
    if (classified === 'classified' && item.category == null) return false;
    if (classified === 'unclassified' && item.category != null) return false;
    if (classified === 'disagreement' && !isDisagreement(item)) return false;
    if (needle) {
      const haystack = `${item.gl_account_name} ${item.gl_account_code ?? ''}`.toLowerCase();
      if (!haystack.includes(needle)) return false;
    }
    return true;
  });
}

const collator = new Intl.Collator('en', { sensitivity: 'base' });

export function sortGlAccounts(
  items: GlAccountOut[],
  sortKey: GlAccountSortKey,
): GlAccountOut[] {
  const byVolume = (a: GlAccountOut, b: GlAccountOut) =>
    Number(b.total_debit) - Number(a.total_debit);
  const sorted = [...items];
  switch (sortKey) {
    case 'volume':
      // The workbench default: unclassified first (they're the work),
      // then dollar volume descending within each group.
      sorted.sort((a, b) => {
        const aUnclassified = a.category == null ? 0 : 1;
        const bUnclassified = b.category == null ? 0 : 1;
        if (aUnclassified !== bUnclassified) return aUnclassified - bUnclassified;
        return byVolume(a, b) || collator.compare(a.gl_account_name, b.gl_account_name);
      });
      break;
    case 'name':
      sorted.sort((a, b) => collator.compare(a.gl_account_name, b.gl_account_name));
      break;
    case 'transactions':
      sorted.sort(
        (a, b) =>
          b.transaction_count - a.transaction_count ||
          collator.compare(a.gl_account_name, b.gl_account_name),
      );
      break;
    case 'lastSeen':
      sorted.sort(
        (a, b) =>
          Date.parse(b.last_seen_at) - Date.parse(a.last_seen_at) ||
          collator.compare(a.gl_account_name, b.gl_account_name),
      );
      break;
  }
  return sorted;
}

/** Client-side search/filter/sort state for the classification
 *  workbench table. Pure derivation over the fetched rows — the
 *  server's page order is replaced wholesale so header clicks are
 *  instant. */
export function useGlAccountWorkbench(items: GlAccountOut[]) {
  const [search, setSearch] = useState('');
  const [accountType, setAccountType] = useState('all');
  const [classified, setClassified] = useState<ClassifiedFilter>('all');
  const [sortKey, setSortKey] = useState<GlAccountSortKey>('volume');

  const accountTypes = useMemo(() => distinctAccountTypes(items), [items]);
  const visible = useMemo(
    () => sortGlAccounts(filterGlAccounts(items, { search, accountType, classified }), sortKey),
    [items, search, accountType, classified, sortKey],
  );

  return {
    visible,
    accountTypes,
    search,
    setSearch,
    accountType,
    setAccountType,
    classified,
    setClassified,
    sortKey,
    setSortKey,
  };
}
